import maplibregl from 'maplibre-gl';
import BaseProduct from './BaseProduct';
import LayerManager from './LayerManager';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl } from '../config';

export class SnowAccumLayer extends BaseProduct {
  public readonly id: ProductId = 'snow_accum';
  private unsubscribeStore: (() => void) | null = null;
  private currentMode: 'cumulative' | 'daily' = 'cumulative';
  private currentHours: number = 24;
  private currentDay: number = 1;
  private currentModel: string = 'ecmwf';

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);

    this.unsubscribeStore = store.subscribe((state) => {
      const modeChanged = this.currentMode !== state.snowAccumMode;
      const hoursChanged = this.currentHours !== state.snowAccumHours;
      const dayChanged = this.currentDay !== state.snowAccumDay;
      const modelChanged = this.currentModel !== state.snowAccumModel;

      if (modeChanged || hoursChanged || dayChanged || modelChanged) {
        this.currentMode = state.snowAccumMode || 'cumulative';
        this.currentHours = state.snowAccumHours || 24;
        this.currentDay = state.snowAccumDay || 1;
        this.currentModel = state.snowAccumModel || 'ecmwf';

        if (this.active) {
          this.refreshRaster();
        }
      }

      if (state.snowAccumEnabled !== this.active) {
        if (state.snowAccumEnabled) {
          this.activate();
        } else {
          this.deactivate();
        }
      }
    });
  }

  protected onActivate(): void {
    this.refreshRaster();
  }

  protected onDeactivate(): void {
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.layerManager.removeLayer(this.id);
  }

  public destroy(): void {
    this.onDeactivate();
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    if (this.active) {
      this.refreshRaster();
    }
  }

  public refresh(): void {
    this.refreshRaster();
  }

  private refreshRaster(): void {
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const todayStr = new Date().toISOString().split('T')[0];
    let dateStr = state.currentTimeStep ? state.currentTimeStep.toISOString().split('T')[0] : todayStr;

    if (dateStr > todayStr) {
      dateStr = '';
    }

    const model = state.snowAccumModel || 'ecmwf';
    let intervalKey: string;
    let cleanDate = '';

    intervalKey = (state.snowAccumMode === 'daily') ? `day${state.snowAccumDay || 1}` : `${state.snowAccumHours || 24}h`;
    const availMap = manifest?.snow_accum?.available_by_model?.[model] || manifest?.snow_accum?.available?.[model];
    if (availMap && typeof availMap === 'object') {
      const dates = Object.keys(availMap).filter(d => d !== 'latest').sort();
      if (dates.length > 0 && dateStr && !dates.includes(dateStr)) {
        const pastDates = dates.filter(d => d <= dateStr);
        dateStr = pastDates.length > 0 ? pastDates[pastDates.length - 1] : dates[dates.length - 1];
      }
    }
    cleanDate = dateStr ? dateStr.replace(/-/g, '') : '';

    const tiles = [
      getTileUrl(manifest, this.id, model, cleanDate, intervalKey)
    ];

    this.layerManager.updateBufferedRasterLayer(this.id, tiles, opacity, undefined, 0, 6);
  }
}

export default SnowAccumLayer;
